<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useDebounceFn, breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { useRoute } from "vue-router/composables";
import { ButtonSeverity, isNullish, TuButton } from "@tinyurldev/tu-ui-vue";

import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue";
import { XTeamsStore } from "@Modules/teams/Store/XTeamsStore.ts";
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel.ts";
import type { PendingTeamInvitationData } from "@Modules/teams/Data/Models/PendingTeamInvitation";
import type { TeamUser } from "@Modules/teams/Data/Models/TeamUserModel";
import SettingsTopBar from "@Modules/teams/components/SettingsTopBar.vue";
import OrgUserActiveTable from "@Modules/teams/components/OrgUserActiveTable.vue";
import OrgUserPendingTable from "@Modules/teams/components/OrgUserPendingTable.vue";
import InviteUserModal from "@Modules/teams/Modals/InviteUserModal.vue";
import UsersApi from "@Modules/users/Api/UsersApi";
import InvitationsApi from "@Modules/teams/Api/InvitationsApi";
import XOverlay from "@Modules/shared/store/XOverlay";
import OrgSettingsContent from "@Modules/teams/components/OrgSettingsContent.vue";
import DeactivateUserModal from "@Modules/teams/Modals/DeactivateUserModal.vue";
import { logError } from "@Resources/js/helpers/logger";

//
// Setup
const route = useRoute();
const teams = XTeamsStore();
const overlay = XOverlay();
const usersApi = UsersApi();
const invitationsApi = InvitationsApi();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);

//
// Computed
const isMobileView = breakpoints.smallerOrEqual("xl");

/** The slug of the current organization and team */
const orgSlug = computed<string>(() => route?.params?.org || "");
const teamSlug = computed<string | null>(() => route.params?.team || null);

/** The currently loaded organization */
const currentOrganization = computed<Organization | null>(() =>
  teams.getOrganizationBySlug(orgSlug.value),
);

/** The currently chosen workspace. Personal team is 'personal'. Null if no workspace chosen */
const currentWorkspace = computed(() => {
  if (isNullish(teamSlug.value)) {
    return null;
  }

  return teams.getWorkspaceBySlugAndTeam(orgSlug.value, teamSlug.value) ?? null;
});

/** Loading state - true if either API is still loading */
const isLoading = computed<boolean>(
  () => usersApi.loading.value || invitationsApi.loading.value,
);

//
// Methods

/** Open the confirmation that deactivates a member of the organization. */
const handleDeactivateUser = (user: TeamUser) => {
  const organization = currentOrganization.value;

  if (isNullish(organization)) {
    logError("[OrgUserSettingsController]: Cannot deactivate user, organization is undefined.");

    return;
  }

  if (isNullish(user.email)) {
    logError("[OrgUserSettingsController]: Cannot deactivate user, email is undefined.");

    return;
  }

  overlay.showModal(DeactivateUserModal, {
    user,
    deactivateUser: () => usersApi.removeOrgUser({
      orgID: organization.slug,
      email: user.email,
    }),
  });
};

/** Resend a pending invitation. Debounced to avoid duplicate sends from rapid clicks. */
const resendInvitation = useDebounceFn(async (user: PendingTeamInvitationData) => {
  if (isNullish(currentOrganization.value)) {
    logError("[OrgUserSettingsController]: Cannot resend invitation, organization is undefined.");

    return;
  }

  await invitationsApi.resendInvitation({
    orgID: currentOrganization.value.slug,
    ...(user.team_slug && { teamID: user.team_slug }),
    invitationID: user.invitation_id,
  });
}, 500);

/** Cancel a pending invitation. */
const cancelInvitation = async (user: PendingTeamInvitationData) => {
  if (isNullish(currentOrganization.value)) {
    logError("[OrgUserSettingsController]: Cannot cancel invitation, organization is undefined.");

    return;
  }

  await invitationsApi.cancelInvitation({
    orgID: currentOrganization.value.slug,
    invitationID: user.invitation_id,
    ...(user.team_slug && { teamID: user.team_slug }),
  });
};

const inviteUser = () => {
  if (isNullish(currentOrganization.value)) {
    logError("[OrgUserSettingsController]: Cannot invite user, organization is undefined.");

    return;
  }

  const orgID = currentOrganization.value.slug;

  overlay.showModal(InviteUserModal, {
    organization: currentOrganization.value,
    existingEmails: usersApi.users.value.map((user) => user.email),
    inviteUsers: async (emails: string[]) => invitationsApi.inviteUsers({ orgID, emails }),
    notifyInvited: () => invitationsApi.fetchInvitations({ orgID }),
    clearErrors: invitationsApi.clearErrors,
    errors: invitationsApi.errors,
  });
};

onMounted(() => {
  if (
    currentOrganization.value &&
    currentOrganization.value.can("add_org_user")
  ) {
    usersApi.fetchOrgUsers({ orgID: currentOrganization.value.slug });
    invitationsApi.fetchInvitations({
      orgID: currentOrganization.value.slug,
    });
  }
});
</script>

<template>
  <OrgSettingsLayout
    v-if="currentOrganization"
    class="user-settings"
    :organization="currentOrganization"
    :workspace="currentWorkspace"
  >
    <div class="user-settings__content">
      <SettingsTopBar title="Users">
        <TuButton
          v-if="currentOrganization.can('add_org_user')"
          class="_invite"
          label="Invite"
          icon-class="fa-solid fa-plus"
          :severity="ButtonSeverity.SECONDARY"
          :is-block="isMobileView"
          @click="inviteUser"
        />
      </SettingsTopBar>

      <OrgSettingsContent
        v-if="currentOrganization.can('add_org_user')"
        title="Active"
        description="Accepted invites with current access to this workspace."
      >
        <OrgUserActiveTable
          :organization="currentOrganization"
          :users="usersApi.users.value"
          :loading="isLoading"
          :deactivate="handleDeactivateUser"
        />
      </OrgSettingsContent>

      <OrgSettingsContent
        v-if="currentOrganization.can('add_org_user')"
        title="Pending"
        description="Invited but not yet accepted. You can resend or cancel."
      >
        <OrgUserPendingTable
          :organization="currentOrganization"
          :users="invitationsApi.invitations.value"
          :loading="invitationsApi.loading.value"
          :resend-invitation="resendInvitation"
          :cancel-invitation="cancelInvitation"
        />
      </OrgSettingsContent>
    </div>
  </OrgSettingsLayout>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.user-settings {
  &__content {
    display: flex;
    flex-flow: column;
    gap: spacing.$s;
  }
}
</style>
